import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface RequireSuperAdminProps {
  children: React.ReactNode;
}

/**
 * Gate for `/app/superadmin/*` routes. Mirrors RequireAdmin but:
 *   - Reads `users.is_super_admin` instead of `users.is_admin`.
 *   - Sends non-super-admins to `/app/dashboard` (NEVER to /login).
 *     A regular user hitting /app/superadmin should not get any signal that
 *     a super-admin route exists — bouncing to login would advertise it.
 *
 * This is a UX gate only. Every super-admin mutation still has to round-trip
 * through an edge function that re-checks `is_super_admin` server-side with
 * the caller's JWT.
 */
export const RequireSuperAdmin: React.FC<RequireSuperAdminProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [checkingSuperAdmin, setCheckingSuperAdmin] = useState<boolean>(true);

  useEffect(() => {
    const checkSuperAdminStatus = async () => {
      if (!user) {
        setCheckingSuperAdmin(false);
        return;
      }

      try {
        // `is_super_admin` is added by migration 20260513170000; the generated
        // Supabase types may not list it yet on the developer's machine, so
        // we cast through `unknown` to avoid a typecheck error until Sergio
        // regenerates types.
        const { data, error } = await supabase
          .from('users')
          .select('is_super_admin')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error checking super admin status:', error);
          setIsSuperAdmin(false);
        } else {
          const flag = (data as unknown as { is_super_admin?: boolean } | null)
            ?.is_super_admin;
          setIsSuperAdmin(!!flag);
        }
      } catch (error) {
        console.error('Error checking super admin status:', error);
        setIsSuperAdmin(false);
      } finally {
        setCheckingSuperAdmin(false);
      }
    };

    if (!loading) {
      checkSuperAdminStatus();
    }
  }, [user, loading]);

  if (loading || checkingSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Unauthenticated users go through the normal auth flow. RequireAuth
    // wraps this gate in App.tsx, so we should not reach this branch in
    // practice — keep it as a defensive fallback.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isSuperAdmin) {
    // Bounce to the dashboard rather than /login so the route's existence
    // stays invisible to anyone who isn't already a super admin.
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
};
